import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { _ti, _tk } from '@wephone-translation';
import { FlexBasePage } from '@wephone-core-ui';
import { EnterpriseEntity } from '@wephone-core/model/entity/enterprise';
import { DialogService, DynamicFilterSource, IFlexTableConfig } from '@wephone-utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { NumberLocationEntity } from '@wephone-core/model/entity/number_location';
import { EntityManager } from '@wephone-core/wephone-core.module';
import { NumberLocationRepository } from '@wephone-core/model/repository/number_location';
import { NumberLocationEditComponent } from '../number-location-edit/number-location-edit.component';
import { AuthenticationService } from '@wephone-core/service/authentication';


@Component({
  selector: 'app-number-locations',
  templateUrl: './number-locations.component.html',
  styleUrls: ['./number-locations.component.scss']
})
export class NumberLocationsComponent extends FlexBasePage {
  @Input() readonly enterprise: EnterpriseEntity;

  dataSourcePrimary: DynamicFilterSource;
  dataSourceSecondary: DynamicFilterSource;
  tableConfig: IFlexTableConfig;

  private _dataSource: BehaviorSubject<NumberLocationEntity[]>;
  private isSuperAdminOrReseller: boolean;
  private numberLocationRepo = this.em.getRepository<NumberLocationRepository>('NumberLocationRepository');

  constructor(
    private readonly dialogService: DialogService,
    private readonly em: EntityManager,
    readonly authService: AuthenticationService,
    cdr: ChangeDetectorRef
  ) {
    super(cdr);

    this.isSuperAdminOrReseller = this.authService.isSuperAdmin() || this.authService.isAdmin() && this.authService.isBusinessReseller();

    this._dataSource = new BehaviorSubject<NumberLocationEntity[]>([]);

    this.tableConfig = {
      scrollable: false,
      multiSelect: false,
      enableFilter: false,
      enableExportCsv: false,
      columns: [
        {
          name: 'name',
          label: 'public.name',
          sortable: true,
          searchable: true
        },
        {
          name: 'address',
          label: 'input.address.label',
          sortable: true,
          searchable: true
        },
        {
          name: 'insee_code',
          label: 'client.content.insee_code',
          sortable: true,
          searchable: true,
          width: '150px'
        },
        {
          name: 'action',
          label: 'public.action',
          searchable: false,
          width: '150px'
        }
      ]
    };

  }

  ngOnInit(): void {
    super.ngOnInit();

    const ds = this.getDataSource<NumberLocationEntity>();

    this.dataSourcePrimary = new DynamicFilterSource(ds, this.filterPrimary);
    this.dataSourceSecondary = new DynamicFilterSource(ds, this.filterSecondary);
  }

  private readonly filterPrimary = (item: NumberLocationEntity): boolean => {
    return !!item.is_default;
  }

  private readonly filterSecondary = (item: NumberLocationEntity): boolean => {
    return !item.is_default;
  }

  getDataSource<T extends NumberLocationEntity = any>(): Observable<T[]> {
    return this._dataSource.asObservable() as Observable<T[]>;
  }

  async resolveData(): Promise<void> {
    await this.reloadData();
  }

  async reloadData(): Promise<void> {
    let list: NumberLocationEntity[];

    if (this.isSuperAdminOrReseller) {
      list = await this.numberLocationRepo.findByEnterpriseId(this.enterprise.id);
    } else {
      list = await this.numberLocationRepo.loadObjectList();
    }

    this._dataSource.next(list);
  }

  deleteNumberLocation(item: NumberLocationEntity): void {
    // if (item.is_default) {
    //   console.error('Not allow delete default address');
    //   return;
    // }

    this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('user.title.delete'),
      async () => {
        try {
          await this.numberLocationRepo.bulkDelete([item]);
          this.reloadData();
          this.detectChanges();
          this.successToast(_ti('public.message.delete_success'));
        } catch (error) {
          this.showErrorMessage(error, _ti('public.message.delete_failure'));
        }
      });
  }

  createNumberLocation(isDefault: boolean): void {
    this.dialogService.openDialog3(_tk('number_location.action.create'), NumberLocationEditComponent, {
      data: {
        item: new NumberLocationEntity(this.enterprise.id, isDefault)
      }
    },
      (resp: any) => {
        this.reloadData();
        this.detectChanges();
      });
  }

  editNumberLocation(item: NumberLocationEntity): void {
    this.dialogService.openDialog3(_tk('number_location.action.edit'), NumberLocationEditComponent, {
      data: {
        item
      }
    },
      (resp: any) => {
        this.reloadData();
        this.detectChanges();
      });
  }

  async makeNumberLocationAsPrimary(item: NumberLocationEntity): Promise<void> {
    try {
      const enterpriseId = this.isSuperAdminOrReseller ? this.enterprise.id : undefined;
      await this.numberLocationRepo.makeNumberLocationAsPrimary(item, enterpriseId);
      this.reloadData();
      this.detectChanges();
      this.successToast(_ti('public.message.update_success'));
    } catch (error) {
      this.showErrorMessage(error, _ti('public.message.update_failure'));
    }
  }
}
